// Выход в сеть за агрегатами аналитики и рейтинга.
//
// Почему отдельный файл, а не api/client.ts + api/hooks.ts. Те два — общий контракт CRM,
// у них свой владелец, и подмешивать туда два новых эндпоинта значило бы править чужое.
// Как только аналитика приживётся, всё отсюда переезжает туда одним куском.
//
// ЧЕМ ЭТИ ЗАПРОСЫ ОТЛИЧАЮТСЯ ОТ ОСТАЛЬНЫХ
//
// Все прочие экраны спрашивают у ядра СТРАНИЦУ записей (`POST /api/bx`, 50 строк) и любую
// сумму считают в браузере — поэтому под каждой суммой в CRM написано «по N из M». Здесь
// наоборот: считает ядро (`analytics_api.py`), и приходят уже итоговые числа по ВСЕЙ
// выборке. Это единственные точные суммы в CRM, и ровно поэтому раздел вообще понадобился:
// в ядре 74 655 сделок (28.07.2026), и «аналитика по 50 записям» — это не аналитика.
//
// ЗОНУ ВИДИМОСТИ ЗДЕСЬ НЕ ПЕРЕДАЁМ И НЕ МОЖЕМ
//
// Ни `scope`, ни `users` в этих параметрах нет намеренно. Кого человеку разрешено видеть,
// решает BFF (`web/bff/rights.py`) и дописывает в тело запроса сам, уже на сервере. Если
// бы зону присылал браузер, её можно было бы подменить — и рейтинг стал бы способом
// посмотреть чужие цифры. Ядро без `scope` отвечает 400, то есть забыть его нельзя.

import { ApiError, Unauthorized } from './client'

/**
 * BFF ещё не знает про эти маршруты (их добавляет владелец `web/bff/**`).
 *
 * Отдельный тип ошибки, а не общий текст: 404 здесь означает «раздел не подключён», и
 * человеку надо сказать именно это. «Ошибка: HTTP 404» на экране аналитики выглядит как
 * поломка данных, хотя данные целы.
 */
export class NotWired extends Error {
  constructor() {
    super('раздел аналитики ещё не подключён в BFF')
    this.name = 'NotWired'
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  })
  if (res.status === 401) throw new Unauthorized()
  if (res.status === 404) throw new NotWired()
  const text = await res.text()
  const data: unknown = text ? JSON.parse(text) : {}
  if (!res.ok) {
    const rec = (data ?? {}) as Record<string, unknown>
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    // 504 от ядра — «агрегат не уложился в лимит». Это не пустые данные и не поломка,
    // а слишком широкий период; текст с подсказкой ядро присылает готовым.
    const hint = rec.hint ? ` ${rec.hint as string}` : ''
    throw new ApiError(msg + hint, res.status)
  }
  return data as T
}

// ─────────── что спрашиваем ───────────

/** Период. Смысл границ считает ядро; здесь только названия для выпадающего списка. */
export type PeriodKind = 'month' | 'prev_month' | 'quarter' | 'year' | 'prev_year' | 'all'

export interface AnalyticsParams {
  period?: PeriodKind
  /** Своя пара границ. `to` — исключающая. Задаются только вместе. */
  from?: string
  to?: string
  /** Номер воронки Bitrix или 'all'. */
  category?: number | 'all'
  /** Идентификатор значения бизнес-юнита, 'empty' — «не заполнен», 'all' — не фильтровать. */
  unit?: string | 'empty' | 'all'
  /** Отдел Bitrix: считается вместе с подотделами. */
  dept?: number | 'all'
  /** Сколько месяцев в ряду динамики (1..36). Только для аналитики продаж. */
  months?: number
}

// ─────────── что приходит ───────────

export interface Period {
  kind: string
  from: string
  to: string
  label: string
  prev_from: string | null
  prev_to: string | null
  prev_label: string
}

export interface ScopeInfo {
  kind: 'all' | 'ids'
  count: number | null
  empty: boolean
}

export interface DeptInfo {
  dept_id: number
  depts: number
  users: number[]
  users_active: number
}

export interface FilterInfo {
  category: number | 'all'
  unit: string
  dept: DeptInfo | null
  scope: ScopeInfo
}

/**
 * Итог с честным окружением. Сделки в чужой валюте с 27.07.2026 в сумму ВХОДЯТ: в сумме
 * сделки бизнес-процесс Bitrix записывает уже рубли (пересчёт делает БП 685), и обнуление
 * отнимало у аналитики 101,8 млн ₽ ни за что. Счётчик остался — «мы посчитали их как
 * рубли» это утверждение, и оно должно быть видно рядом с числом сделок.
 */
export interface MoneyTotal {
  count: number
  sum: number
  other_currency_count: number
  other_currencies: string[]
}

export interface StageRow {
  stage_id: string
  count: number
  sum: number
  semantics: string
}

export interface UserRow {
  user_id: number | null
  count: number
  sum: number
}

export interface ClosedSummary {
  won: MoneyTotal
  lost: MoneyTotal
  closed_count: number
  closed_sum: number
  /** null — выигранных сделок нет; ноль здесь читался бы как «чек нулевой». */
  avg_check: number | null
  conversion_count: number | null
  conversion_sum: number | null
}

export interface MonthRow {
  month: string
  won_count: number
  won_sum: number
  lost_count: number
  lost_sum: number
  conversion_count: number | null
  avg_check: number | null
}

export interface UnitRow {
  unit: string
  empty: boolean
  count: number
  sum: number
}

export interface SalesAnalytics {
  ok: true
  block: 'sales'
  period: Period
  filter: FilterInfo
  months_from: string
  portfolio: MoneyTotal & { by_stage: StageRow[]; by_user: UserRow[] }
  closed: ClosedSummary
  closed_prev: ClosedSummary | null
  dynamics: {
    won_count: number | null
    won_sum: number | null
    avg_check: number | null
    conversion_count_pp: number | null
  } | null
  by_month: MonthRow[]
  by_category: Array<{ category_id: number | null; count: number; sum: number }>
  by_unit: UnitRow[]
  by_user: UserRow[]
  stage_semantics: Record<string, string>
  /** Готовые к показу оговорки. Выводить как есть — они объясняют расхождения с порталом. */
  notes: string[]
  ms: number
}

/**
 * Строка рейтинга.
 *
 * ЧТО ЗДЕСЬ ЗА ПРИБЫЛЬ. С 27.07.2026 `profit` — это поле «Разница» (UF_CRM_1486892924),
 * которое записал в сделку бизнес-процесс «Маржа», в рублях. Раньше ядро считало её само
 * как `оборот × «Процент прибыли» / 100`, и из этого выходили три неверных ответа сразу:
 * округлённый процент вместо рублей, зажим в [0; 100], превращавший убыток в ноль, и вся
 * прибыль одному ответственному без доли сервиса. Разбор — `cir-core/БП_52_маржа.md` §7.
 *
 * Из-за перехода `profit` и `margin` МОГУТ БЫТЬ ОТРИЦАТЕЛЬНЫМИ. Это не сбой: 790 сделок
 * зеркала закрыты в минус, и минус обязан выглядеть минусом.
 */
export interface RatingRow {
  user_id: number | null
  deals: number
  revenue: number
  /** «Разница» из Bitrix. Может быть отрицательной — сделки закрывают и в минус. */
  profit: number
  /** Доля отдела продаж («Разница ОП»), как её посчитал бизнес-процесс. */
  profit_op: number
  /** Доля сервиса («Разница Сервис»). Её рейтинг раньше отдавал продавцу целиком. */
  profit_service: number
  /** Сделки, где бизнес-процесс прибыль не посчитал вовсе: принят ноль, оборот остался. */
  no_profit: number
  /** Сделки, закрытые в минус. */
  loss_deals: number
  /** Сделки, где деление прибыли на ОП/Сервис записано (оно есть не у всех). */
  split_deals: number
  /** Сделки, где «Разница» ≠ Сумма − Затраты − Налоги: поля самого БП не сходятся. */
  mismatch: number
  other_currency_count: number
  /** Прибыль по ПРЕЖНЕЙ формуле — только чтобы объяснить разницу числом. */
  prev_formula_profit: number
  no_pct: number
  pct_clamped: number
  place: number
  medal: string
  margin: number | null
  avg_profit: number | null
  share_of_leader: number | null
  league: string
  prev_profit: number | null
  prev_place: number | null
  profit_growth: number | null
  /** Насколько поднялся в таблице. null — в прошлом периоде его в рейтинге не было. */
  place_moved: number | null
}

export interface SalesRating {
  ok: true
  block: 'rating'
  period: Period
  filter: FilterInfo
  rows: RatingRow[]
  total: {
    deals: number
    revenue: number
    profit: number
    profit_op: number
    profit_service: number
    no_profit: number
    loss_deals: number
    split_deals: number
    mismatch: number
    other_currency_count: number
    margin: number | null
    prev_profit: number | null
    profit_growth: number | null
    /**
     * Прежний расчёт целиком, отдельным узлом. Ядро считает его тем же запросом ровно
     * ради одного: экран обязан сказать «было столько-то, стало столько-то». Рейтинг,
     * который однажды утром стал другим без объяснения, стоит доверия ко всей CRM
     * дороже, чем любая верная формула. Узел временный и уйдёт целиком.
     */
    prev_formula: {
      profit: number
      /** Новая прибыль минус прежняя. Отрицательное — стало меньше. */
      delta: number
      no_pct: number
      pct_clamped: number
      field: string
    }
  }
  /** Код поля, из которого взята прибыль. Сверка с порталом идёт по коду, а не по названию. */
  profit_field: string
  /** Коды всех полей расчёта: прибыль, доли, затраты, налоги и прежняя формула. */
  profit_fields: {
    profit: string
    op: string
    service: string
    costs: string
    taxes: string
    prev_formula: string
  }
  notes: string[]
  ms: number
}

export const fetchSalesAnalytics = (p: AnalyticsParams) =>
  post<SalesAnalytics>('/api/analytics/sales', p)

export const fetchSalesRating = (p: AnalyticsParams) =>
  post<SalesRating>('/api/analytics/rating', p)
